<template>
    <div class="space-y-6">
        <div class="space-y-4">
            <div class="flex items-center justify-between">
                <h2 class="text-lg font-semibold text-gray-900">Top Sources (Top 25)</h2>
                <span class="text-sm text-gray-500">Grouped by company source owners for current registrants</span>
            </div>
            <div class="overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <tr>
                            <th scope="col" class="px-6 py-3">Source Owner</th>
                            <th scope="col" class="px-6 py-3">Email</th>
                            <th scope="col" class="px-6 py-3">Registrants</th>
                            <th scope="col" class="px-6 py-3">Share</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 bg-white">
                        <tr v-if="sourceSummaryRows.length === 0">
                            <td colspan="4" class="px-6 py-6 text-center text-sm text-gray-500">
                                No source owners found for current registrants.
                            </td>
                        </tr>
                        <tr
                            v-for="row in sourceSummaryRows"
                            :key="row.id"
                            class="transition-colors hover:bg-gray-50"
                        >
                            <td class="px-6 py-4 text-sm font-medium text-gray-900">{{ row.name }}</td>
                            <td class="px-6 py-4 text-sm text-gray-700">
                                <template v-if="row.email">
                                    <a :href="`mailto:${row.email}`" class="mf-link">{{ row.email }}</a>
                                </template>
                                <template v-else>
                                    <span class="text-gray-500">N/A</span>
                                </template>
                            </td>
                            <td class="px-6 py-4 text-sm font-semibold text-gray-900">{{ row.count }}</td>
                            <td class="px-6 py-4 text-sm text-gray-700">{{ row.percentage }}%</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
                <h2 class="text-lg font-semibold text-gray-900">Registered Participants</h2>
                <p class="text-sm text-gray-500">
                    Showing {{ filteredRegistrations.length }} of {{ totalRegistrations }} registrants
                </p>
            </div>
            <div class="relative w-full md:w-72">
                <input
                    v-model="searchTerm"
                    type="text"
                    class="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
                    placeholder="Search name, email, company, or source"
                />
                <span class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                    <i class="fa-solid fa-magnifying-glass"></i>
                </span>
            </div>
        </div>

        <div class="rounded-2xl border border-gray-200 shadow-sm">
            <div class="overflow-x-auto">
                <table class="min-w-full min-w-[960px] divide-y divide-gray-200 text-sm">
                <thead class="bg-gray-50 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                        <th scope="col" class="px-3 py-2">
                            <button
                                type="button"
                                class="flex items-center gap-2 text-left uppercase tracking-wide text-gray-500 transition hover:text-gray-700"
                                @click="setSort('name')"
                            >
                                Name
                                <i :class="getSortIconClass('name')"></i>
                            </button>
                        </th>
                        <th scope="col" class="px-3 py-2">
                            <button
                                type="button"
                                class="flex items-center gap-2 text-left uppercase tracking-wide text-gray-500 transition hover:text-gray-700"
                                @click="setSort('email')"
                            >
                                Email
                                <i :class="getSortIconClass('email')"></i>
                            </button>
                        </th>
                        <th scope="col" class="px-3 py-2">
                            <button
                                type="button"
                                class="flex items-center gap-2 text-left uppercase tracking-wide text-gray-500 transition hover:text-gray-700"
                                @click="setSort('company')"
                            >
                                Company
                                <i :class="getSortIconClass('company')"></i>
                            </button>
                        </th>
                        <th scope="col" class="px-3 py-2">
                            <button
                                type="button"
                                class="flex items-center gap-2 text-left uppercase tracking-wide text-gray-500 transition hover:text-gray-700"
                                @click="setSort('source')"
                            >
                                Source Owner
                                <i :class="getSortIconClass('source')"></i>
                            </button>
                        </th>
                        <th scope="col" class="px-3 py-2">
                            <button
                                type="button"
                                class="flex items-center gap-2 text-left uppercase tracking-wide text-gray-500 transition hover:text-gray-700"
                                @click="setSort('hasPro')"
                            >
                                Expert Pro
                                <i :class="getSortIconClass('hasPro')"></i>
                            </button>
                        </th>
                        <th scope="col" class="px-3 py-2">
                            <button
                                type="button"
                                class="flex items-center gap-2 text-left uppercase tracking-wide text-gray-500 transition hover:text-gray-700"
                                @click="setSort('hasJoined')"
                            >
                                Joined
                                <i :class="getSortIconClass('hasJoined')"></i>
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-100 bg-white text-xs">
                    <tr v-if="filteredRegistrations.length === 0">
                        <td colspan="6" class="px-4 py-4 text-center text-gray-500">
                            No registrants match your search.
                        </td>
                    </tr>
                    <tr
                        v-for="registration in filteredRegistrations"
                        :key="registration.id"
                        class="transition-colors hover:bg-gray-50"
                    >
                        <td class="px-3 py-2 font-medium text-gray-900">
                            {{ registration.name }}
                        </td>
                        <td class="px-3 py-2 text-gray-700">
                            <template v-if="registration.email">
                                <a :href="`mailto:${registration.email}`" class="mf-link">
                                    {{ registration.email }}
                                </a>
                            </template>
                            <template v-else>
                                <span class="text-gray-500">N/A</span>
                            </template>
                        </td>
                        <td class="px-3 py-2 text-gray-700">
                            {{ registration.company ?? 'N/A' }}
                        </td>
                        <td class="px-3 py-2 text-gray-700">
                            <template v-if="registration.sourceUser">
                                <div class="flex flex-col">
                                    <span class="font-medium text-gray-900">{{ registration.sourceUser.name }}</span>
                                    <span class="text-[10px] text-gray-500" v-if="registration.sourceUser.email">{{ registration.sourceUser.email }}</span>
                                </div>
                            </template>
                            <template v-else>
                                <span class="text-gray-500">N/A</span>
                            </template>
                        </td>
                        <td class="px-3 py-2">
                            <span
                                :class="[
                                    'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide',
                                    registration.hasPro
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-gray-100 text-gray-600',
                                ]"
                            >
                                <i
                                    :class="[
                                        'text-[11px]',
                                        registration.hasPro ? 'fa-solid fa-gem' : 'fa-regular fa-circle'
                                    ]"
                                ></i>
                                {{ registration.hasPro ? 'Active' : 'No' }}
                            </span>
                        </td>
                        <td class="px-3 py-2">
                            <span
                                :class="[
                                    'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide',
                                    registration.hasJoined
                                        ? 'bg-sky-100 text-sky-700'
                                        : 'bg-gray-100 text-gray-600',
                                ]"
                            >
                                <i
                                    :class="[
                                        'text-[11px]',
                                        registration.hasJoined ? 'fa-solid fa-circle-check' : 'fa-regular fa-circle'
                                    ]"
                                ></i>
                                {{ registration.hasJoined ? 'Joined' : 'Pending' }}
                            </span>
                        </td>
                    </tr>
                </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

type SourceUser = {
    id: number;
    name: string;
    email: string | null;
};

type Registrant = {
    id: number | string;
    name: string;
    email: string | null;
    company: string | null;
    hasPro: boolean;
    hasJoined: boolean;
    sourceUser: SourceUser | null;
};

type PreparedRegistrant = Registrant;

type SortField = 'name' | 'email' | 'company' | 'source' | 'hasPro' | 'hasJoined';
type SortDirection = 'asc' | 'desc';

type SourceSummaryRow = {
    id: number;
    name: string;
    email: string | null;
    registrant_count: number;
};

type SourceSummaryDisplayRow = {
    id: number;
    name: string;
    email: string | null;
    count: number;
    percentage: number;
};

const props = defineProps<{
    registrations: Registrant[];
    sourceSummary: SourceSummaryRow[];
}>();

const searchTerm = ref('');
const sortField = ref<SortField>('name');
const sortDirection = ref<SortDirection>('asc');

const preparedRegistrations = computed<PreparedRegistrant[]>(() => {
    return props.registrations.map((registration) => ({
        ...registration,
        name: registration.name ?? 'N/A',
        email: registration.email ?? null,
        company: registration.company ?? null,
    }));
});

const totalRegistrations = computed(() => preparedRegistrations.value.length);

const fieldAccessors: Record<SortField, (registration: PreparedRegistrant) => string | number> = {
    name: (registration) => registration.name.toLowerCase(),
    email: (registration) => (registration.email ?? '').toLowerCase(),
    company: (registration) => (registration.company ?? '').toLowerCase(),
    source: (registration) => (registration.sourceUser?.name ?? '').toLowerCase(),
    hasPro: (registration) => (registration.hasPro ? 1 : 0),
    hasJoined: (registration) => (registration.hasJoined ? 1 : 0),
};

const compareRegistrations = (a: PreparedRegistrant, b: PreparedRegistrant): number => {
    const field = sortField.value;
    const directionMultiplier = sortDirection.value === 'asc' ? 1 : -1;

    const valueA = fieldAccessors[field](a);
    const valueB = fieldAccessors[field](b);

    if (valueA < valueB) {
        return -1 * directionMultiplier;
    }

    if (valueA > valueB) {
        return 1 * directionMultiplier;
    }

    return a.name.localeCompare(b.name);
};

const filteredRegistrations = computed(() => {
    const term = searchTerm.value.trim().toLowerCase();

    const dataset = term
        ? preparedRegistrations.value.filter((registration) => {
              return [
                  registration.name,
                  registration.email ?? '',
                  registration.company ?? '',
                  registration.sourceUser?.name ?? '',
                  registration.sourceUser?.email ?? '',
              ].some((field) => field.toLowerCase().includes(term));
          })
        : [...preparedRegistrations.value];

    return dataset.sort(compareRegistrations);
});

const setSort = (field: SortField) => {
    if (sortField.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
        return;
    }

    sortField.value = field;
    sortDirection.value = field === 'hasPro' || field === 'hasJoined' ? 'desc' : 'asc';
};

const getSortIconClass = (field: SortField) => {
    if (sortField.value !== field) {
        return 'fa-solid fa-sort text-gray-400';
    }

    return sortDirection.value === 'asc'
        ? 'fa-solid fa-sort-up text-sky-600'
        : 'fa-solid fa-sort-down text-sky-600';
};

const sourceSummaryRows = computed<SourceSummaryDisplayRow[]>(() => {
    const total = totalRegistrations.value;

    if (total === 0) {
        return [];
    }

    return props.sourceSummary.map((row) => ({
        id: row.id,
        name: row.name,
        email: row.email,
        count: row.registrant_count,
        percentage: Math.round((row.registrant_count / total) * 100),
    }));
});
</script>
